import React from 'react';

export interface BodySellingGrossData {
  bodySales: number;
  bodyCOS: number;
  bodyGross: number;
  salesExpenses: Array<{ accountCode: string; description: string; amount: number }>;
  totalSalesExpenses: number;
  sellingGross: number;
  squeezeApplied?: boolean;
}

const fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });

function formatCurrency(value: number): string {
  if (value < 0) {
    return `(${fmt.format(Math.abs(value))})`;
  }
  return fmt.format(value);
}

interface Props {
  data: BodySellingGrossData;
}

export const BodySellingGrossReport: React.FC<Props> = ({ data }) => {
  const {
    bodySales,
    bodyCOS,
    bodyGross,
    salesExpenses,
    totalSalesExpenses,
    sellingGross,
    squeezeApplied,
  } = data;

  return (
    <div className="overflow-x-auto">
      {squeezeApplied && (
        <div className="text-xs text-gray-500 italic mb-2 px-1">Warranty Squeeze Applied</div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b-2 border-gray-300">
            <th className="text-left py-2 px-3 font-semibold text-gray-700 w-3/4">Description</th>
            <th className="text-right py-2 px-3 font-semibold text-gray-700 w-1/4">Amount</th>
          </tr>
        </thead>
        <tbody>
          {/* Gross section */}
          <Row label="Body Shop Sales (Revenue)" value={bodySales} />
          <Row label="Body Shop Cost of Sales" value={bodyCOS} negative />
          <BoldRow label="BODY SHOP GROSS" value={bodyGross} />

          {/* Separator */}
          <tr><td colSpan={2} className="py-2" /></tr>

          {/* Sales Expenses */}
          <tr>
            <td colSpan={2} className="py-1 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Sales Expenses
            </td>
          </tr>
          {salesExpenses.length > 0 ? (
            salesExpenses.map((exp) => (
              <Row
                key={exp.accountCode}
                label={`${exp.accountCode} - ${exp.description}`}
                value={exp.amount}
                indent
              />
            ))
          ) : (
            <tr>
              <td colSpan={2} className="py-1 px-3 pl-6 text-gray-400 italic">
                No sales expense data for this period
              </td>
            </tr>
          )}
          <BoldRow label="TOTAL SALES EXPENSES" value={totalSalesExpenses} />

          {/* Separator */}
          <tr><td colSpan={2} className="py-2" /></tr>

          {/* Selling Gross */}
          <tr className="bg-blue-50 border-t-2 border-b-2 border-blue-200">
            <td className="py-3 px-3 font-bold text-blue-900 text-base">BODY SHOP DEPT SELLING GROSS</td>
            <td className={`py-3 px-3 text-right font-bold text-base font-mono ${sellingGross < 0 ? 'text-red-600' : 'text-blue-900'}`}>
              {formatCurrency(sellingGross)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

function Row({ label, value, indent, negative }: { label: string; value: number; indent?: boolean; negative?: boolean }) {
  const isNeg = value < 0;
  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className={`py-1.5 px-3 text-gray-700 ${indent ? 'pl-6' : ''}`}>{label}</td>
      <td className={`py-1.5 px-3 text-right font-mono ${(isNeg || negative) ? 'text-red-600' : 'text-gray-900'}`}>
        {formatCurrency(negative ? -Math.abs(value) : value)}
      </td>
    </tr>
  );
}

function BoldRow({ label, value }: { label: string; value: number }) {
  const isNeg = value < 0;
  return (
    <tr className="border-b-2 border-gray-200 bg-gray-50">
      <td className="py-2 px-3 font-semibold text-gray-900">{label}</td>
      <td className={`py-2 px-3 text-right font-semibold font-mono ${isNeg ? 'text-red-600' : 'text-gray-900'}`}>
        {formatCurrency(value)}
      </td>
    </tr>
  );
}
